<div class="header">
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <div class="headline">Dash Enterprise</div>
      </div>
    </div>
    <app-enterprise-tab-navigator></app-enterprise-tab-navigator>
  </div>
</div>

<div class="content">
  <div class="contentInner">
    <h1 class="title">Dash Enterprise Configuration options</h1>
    <p class="intro">All of Dash's options can be configured centrally in the dashboard.  This document lists each
      option and explains how to correctly configure it.</p>

    <h2 class="sectionHeader">Microsoft 365 / Google Workspace connection</h2>
    <p>
      If you would like to have each new device automatically connect to your Microsoft 365 or Google Workspace account when it is added
      to your device group, then you can store the connection credentials in the <b>Calendar provider</b> configuration
      option.  The instructions for storing the credentials vary depending on the type of connection that you have:
    </p>

    <h3>Microsoft 365 Enterprise Application</h3>
    <p>
      If you have configured Dash as an <a routerLink="/guide/o365-enterprise-application">Enterprise Application</a>
      in Microsoft 365, add the <i>Calendar provider</i> option to your configuration and choose
      <i>Microsoft 365 Enterprise Application</i> as the provider type.  Enter the credentials from the
      <i>Enterprise Application</i> in the fields provided: <b>Directory (tenant) ID</b>, <b>Application (client) ID</b>,
      and <b>Client secret</b>.  Please see our <a routerLink="/guide/o365-enterprise-application">Enterprise Application</a>
      documentation for instructions on how to obtain these credentials.
    </p>

    <app-illustration alt="Dash Enterprise calendarProvider, showing a Microsoft Enterprise Application connection" src="/assets/images/dash-enterprise-enterprise-application.png" webpSrc="/assets/images/dash-enterprise-enterprise-application.webp" width="880" height="389" class="illustration centered microsoft"></app-illustration>

    <p>
      Click <i>Test connection</i> after entering your credentials to make sure that Dash can connect to Microsoft 365
      and see your calendars.  After reviewing the list of calendars, click the <i>Test</i> button next to a calendar
      to check if Dash is able to read and write meeting information to the calendar.
    </p>

    <h3>Google Workspace Service Account</h3>
    <p>
      If you have configured Dash as a <a routerLink="/guide/google-service-account">Service Account</a>
      in Google Workspace, add the <i>Calendar provider</i> option to your configuration and choose
      <i>Google Workspace Service Account</i> as the provider type.  Enter the credentials from the
      <i>Service Account</i> in the fields provided: <b>Client email</b>, <b>Admin user email address</b>,
      and <b>Private key</b>.  Please see our <a routerLink="/guide/google-service-account">Enterprise Application</a>
      documentation for instructions on how to obtain these credentials.
    </p>
    <p>
      If you have obtained the credentials JSON file after following the instructions in our guide, you can automatically
      fill the <i>Client email</i> and <i>Private key</i> fields by clicking <i>Load from file</i> and selecting the
      file.  You will still need to fill in the <i>Admin user email address</i> field, which can be the email address of
      any global administrator account in your Google Workspace organization.
    </p>
    <p>
      The <i>Use full room names</i> option allows you to choose between using the full generated name for your meeting
      rooms (e.g. <b>San Francisco HQ-10-A-Beehive Room [VC]</b>), or just the name entered when the room was created
      (e.g. <b>Beehive Room</b>).  The full room name will still be visible when choosing calendars from the Dash app's
      admin interface.
    </p>

    <app-illustration alt="Dash Enterprise calendarProvider, showing a Google Workspace ServiceAccount connection" src="/assets/images/dash-enterprise-google-service-account.png" webpSrc="/assets/images/dash-enterprise-google-service-account.webp" width="866" height="573" class="illustration centered"></app-illustration>

    <p>
      Click <i>Test connection</i> after entering your credentials to make sure that Dash can connect to Microsoft 365
      and see your calendars.  After reviewing the list of calendars, click the <i>Test</i> button next to a calendar
      to check if Dash is able to read and write meeting information to the calendar.
    </p>

    <h3>Microsoft 365 business/personal account, or Google Workspace standard or personal account</h3>
    <p>
      If you have configured Dash as an account in <a routerLink="/guide/o365-service-account">Microsoft 365</a>
      or <a routerLink="/guide/google-personal-account">Google Workspace</a>, add the <i>Calendar provider</i> option to your configuration
      and choose either <i>Microsoft 365 Service Account</i> or <i>Google Workspace Service Account</i> as the provider type.
      As the credentials can only be generated on a device, you will need to perform the following procedure:
    </p>
    <ol>
      <li>Using a device that is already has some centrally managed configuration options, open Dash's settings</li>
      <li>In the <i>Calendar and display</i> section, choose <i>Change</i> to change your connection type, and select
        either <i>Microsoft 365</i> and then <i>Business or Personal Microsoft Account</i> if using Microsoft 365, or
        <i>Google Workspace</i> if using Google Workspace.
      </li>
      <li>Sign in to your account and return to Dash</li>
      <li>You should see a <i>Get credentials</i> button.  Tap it and your credentials will be copied to the clipboard.
        Paste the credentials into an email client to get them off the device, and then paste them into the calendar
        provider configuration in the dashboard.
      </li>
    </ol>
    <app-illustration alt="Dash Enterprise calendarProvider, showing a Google Workspace or personal account connection" src="/assets/images/dash-enterprise-service-account.png" webpSrc="/assets/images/dash-enterprise-service-account.webp" width="866" height="477" class="illustration centered"></app-illustration>
    <p>
      Click <i>Test connection</i> after entering your credentials to make sure that Dash can connect to Microsoft 365
      and read and write meeting information to your calendars.
    </p>

    <h3>Device calendar</h3>
    <p>
      If you are connecting to a local Exchange server, or another service that dash does not support directly connecting
      to, then select <i>Device Calendar</i> as the provider type.
    </p>
    <p class="tip icon single-line">Note that you will need to give Dash permission to access your calendars on your device, which needs to be done on the device itself.</p>


    <h2 class="sectionHeader" id="calendars">Configuring calendars</h2>
    <p>
      It is possible to automatically set the calendar(s) that Dash displays by adding the <i>Calendar</i>  option to
      your configuration (if you have set Dash to display a single room's status as a <i>Room display</i> or <i>Countdown display</i>), or the
      <i>Calendars</i> option (if you have set Dash to display multiple room's status as a <i>Dashboard display</i>, <i>Schedule dashboard display</i> or <i>What's on display</i>).
    </p>
    <p>
      You will need to enter the calendar ID(s) for the room(s) you would like to display:
    </p>
    <p>
      For <b>Microsoft 365 Enterprise Application</b> and <b>Google Workspace Service Account</b> connections, the IDs are the email addresses of the mailbox for each room.
    </p>
    <p>
      For <b>Google Workspace standard or personal account</b>, or <b>Microsoft 365 service account</b> connections, you can find the IDs for your calendars by using our
      <a routerLink="/dash-enterprise-auth" target="_blank">web utility</a> or you can click the <i>Test connection</i>
      button in the calendar provider option and copy the calendar ids from the list of available calendars.
    </p>

    <h3>Renaming calendars</h3>
    <p>
      You can also control the names used for your calendars when displayed in Dash by adding the <i>Rename calendars</i>
      option to your configuration.  Once you have added the option, click <i>Add</i>, enter the id of the calendar that
      you would like to rename, and then enter the new name.  Please note that calendar ID's are case sensitive.
    </p>

    <p class="tip icon">
      These IDs are also used for the <i>Rooms to check</i> configuration option that is described in the <i>optional
      features</i> section of this document.
    </p>

    <h2 class="sectionHeader">Display options</h2>
    <p>
      These options allow you to customize the way Dash is displayed on your device.  Control what information is show, and
      how it is shown by using the options below.
    </p>
    <h3>Display mode</h3>
    <p>
      Dash offers five display modes:</p>
      <ul>
        <li>A <i>room display</i> which shows the status of a single meeting room calendar, along with a schedule view.</li>
        <li>A <i>countdown display</i> which shows the status of a single meeting room calendar, with a countdown timer showing the remaining time for the meeting.</li>
        <li>A <i>dashboard display</i> which shows the status of multiple meeting rooms at once.</li>
        <li>A <i>schedule dashboard display</i> which shows the full schedule of multiple meeting rooms at once.</li>
        <li>A <i>what's on display</i> which shows the current and upcoming meetings from multiple rooms.</li>
      </ul>

    <h3>Display meetings as</h3>
    <p>
      This setting controls the appearance of the schedule section on the right hand side of the main calendar display.
      You can choose from the following options:
    </p>
    <ul>
      <li><b>A calendar view of today’s events</b> - This shows the events for the current day in a calendar view</li>
      <li><b>A list of today’s meetings</b> - This shows the events for the current day in a list view</li>
      <li><b>A list of the next two day’s meetings</b> - This shows the events for the current day and next day in a list view</li>
      <li><b>A list of the next seven day’s meetings</b> - This shows the events for the next seven days in a list view</li>
    </ul>
    <p class="tip icon"><i>Display meetings as</i> only has an effect if the <i>Display mode</i> is set to either <i>Room display</i> or <i>Schedule dashboard display</i>.  See above for more information about the <i>Display mode</i> setting.</p>

    <h3>Show meeting colors</h3>
    <p>If you have chosen a custom color for a meeting in Outlook or Google Calendar, Dash will show the color in the
      schedule view when this option is enabled.</p>

    <h3>Show calendar view button</h3>
    <p>When selected, this will show a calendar button near the bottom of the schedule display (when the <i>Display mode</i> is set to
      <i>Room display</i>), at the bottom of the screen (when the <i>Display mode</i> is set to
      <i>Countdown display</i>) or on each room (when the <i>Display mode</i> is set to <i>Dashboard display</i>, <i>Schedule dashboard display</i>, or <i>What's on</i>).  Tapping the calendar icon will bring up a
      calendar schedule view for the room, showing the room's schedule over multiple days.</p>

    <h3>Status display</h3>
    <p>Choose which information is displayed most prominently on the display.  You can choose from:</p>
    <ul>
      <li><b>Large room name</b> - This shows the room's name in a large font, with the meeting's title appearing in a smaller font below it, and the clock in the bottom left corner of the display</li>
      <li><b>Large meeting title</b> - This shows the current meeting's title (or the room's status if there is no current meeting) in a large font, with the room's name in a smaller font above it, and the clock in the bottom left corner of the display</li>
      <li><b>Large clock</b> - This shows the click in a large font, with the room's name and meeting title in a smaller font around it</li>
    </ul>
    <p class="tip icon">
      This option only has an effect when the <i>Display mode</i> is set to <i>Room display</i>.  See above for more information about the <i>Display mode</i> setting.
    </p>

    <h3>Show large clock on main screen</h3>
    <p>
      This option has been replaced with the Status Display option above.
    </p>

    <h3>Show room attributes on main screen</h3>
    <p>
      If <i>Yes</i> Dash will show the room's capacity and icons for the room's attributes (such as the presence of teleconference equipment or disabled access) if available.  This option only has an effect when using the Office 365 'Enterprise Application' or Google Workspace service account connection methods.
    </p>

    <h3>Show date on main screen</h3>
    <p>
      If <i>Yes</i> then the current date will be displayed in a small font at the bottom of the screen.
    </p>

    <h3>Date format</h3>
    <p>
      The format of date to use on the main screen and in the schedule and meeting details views.
    </p>

    <h3>Show all-day meetings</h3>
    <p>
      If <i>Yes</i> then all-day meetings in a room's calendar will be included in the room's schedule and used when
      calculating if the room is free or available.  If <i>No</i> then all-day meetings will be ignored.
    </p>

    <h3>Display only meetings accepted by the calendar</h3>
    <p>
      If <i>Yes</i> then Dash will show only meetings that have been marked as accepted by the room's calendar.  This is
      useful when connecting to room/equipment mailboxes that can automatically decline double bookings.
    </p>

    <h3>Show room occupancy by color</h3>
    <p>
      Controls how Dash uses color to show the current room occupancy status.  You can control the colors used by
      referring to the <i>Appearance and branding</i> section of this guide.
    </p>
    <ul>
      <li><b>On the background of the schedule</b> - Dash changes the background of the room schedule view, or the background of each room when using the dashboard display mode</li>
      <li><b>On a thin border around the whole display</b> - Dash changes the color of a thin border around the whole display, or around each room when using the dashboard display mode</li>
      <li><b>On a thick border around the whole display</b> - Dash changes the color of a thick border around the whole display, or around each room when using the dashboard display mode</li>
      <li><b>On the background of the whole display</b> - Dash changes the color of the background of the whole display, or the background of each room when using the dashboard display mode.
        Select a partially transparent color to have the background image show through.</li>
    </ul>

    <h3>Dim screen outside of working hours</h3>
    <p>
      If <i>Yes</i> then Dash will dim the main display and display a screensaver when it is outside of working hours.  Use
      this setting in conjunction with the <i>Working hours start at</i> and <i>Working hours end after</i> settings to
      control the times that Dash considers working hours.
    </p>

    <h3>Working hours start at / Working hours end after</h3>
    <p>
      Use these settings to control when Dash activates the screensaver when <i>Dim screen outside of working hours</i>
      is turned on.  Enter the values as times in the 24-hour format (e.g. 18:00).  The times will be interpreted in the
      local timezone of your Dash device.
    </p>

    <h3>Working days</h3>
    <p>
      The days of the week when your office is in use.  Dash will dim the display for the whole day on days not selected,
      when <i>Dim screen outside of working hours</i> is turned on.
    </p>

    <h3>Time format</h3>
    <p>
      Whether times are shown in Dash using a <i>12-hour</i> (with AM/PM) or <i>24-hour</i> format.
    </p>

    <h3>Language</h3>
    <p>
      The language to display in Dash.  For the full list of supported languages, please see our <a routerLink="/guide/usingDash" fragment="languages">documentation.</a>
    </p>

    <h2 class="sectionHeader">Security</h2>
    <h3>Settings passcode</h3>
    <p>
      A 4-digit number that must be entered before opening the settings screen.  Enter any 4-digit number.
    </p>

    <h3>Label meetings with</h3>
    <p>
      For security reasons, you may not want to display the titles of meetings on your Dash display.  You can choose to display
      any of the following as your meeting titles:
    </p>
    <ul>
      <li><b>Meeting title</b> - Display the meeting title as normal</li>
      <li><b>Meeting organizer</b> - Display the name (if available) or email address of the meeting organizer in place of the meeting title</li>
      <li><b>No title or organizer</b> - Dash will display "Room booked" in place of the meeting title</li>
    </ul>

    <h3>Display attendees as</h3>
    <p>
      Control how Dash displays the attendees of your meetings:
    </p>
    <ul>
      <li><b>List and count</b> - A count of attendees is displayed in the schedule view, and a full list of attendees
        is displayed if the user views the meeting's details</li>
      <li><b>Count only</b> - A count of attendees is displayed in the schedule view, and if the user views the meeting's details</li>
      <li><b>No list or count</b> - The meeting's attendees are not displayed</li>
    </ul>

    <h3>Display meeting details</h3>
    <p>
      If <i>Yes</i> then a user can tap on a meeting in the schedule or list view, and view its details (including the
      meeting description, and list of attendees).
    </p>

    <h3>Display meeting description when showing meeting details</h3>
    <p>
      If <i>Yes</i> then the meeting's description or summary will be shown when viewing the meeting details.  If <i>No</i>
      then only the attendees will be shown.
    </p>

    <h3>Enable error reporting</h3>
    <p>
      If Dash encounters an unexpected error, you will see an error report.  Enable
      error reporting to allow the error report to be submitted to us so that we can fix the error on the next
      version of Dash.  Error reports contain basic information about your device, as well as information about the
      error.  Error reports never contain any personal information, and you will be able to review the report
      before submitting.
    </p>

    <h2 class="sectionHeader">Optional features</h2>

    <h3>Require check-in</h3>
    <p>
      If <i>Yes</i>, users will be required to click a button on the screen to check in to each meeting. If a meeting
      has not been checked-in ten minutes after it starts, it will be ended.
    </p>

    <h3>Release room after</h3>
    <p>
      The number of minutes after a meeting's scheduled start time that Dash will wait before ending
      it if it has not been checked-in.  If <i>Don't release</i> is selected, the meeting will be displayed as tentative on
      the display until the check-in button is pressed but Dash won't end the meeting if it isn't checked in.
    </p>

    <h3>Release room from recurring meetings after</h3>
    <p>
      When a recurring meeting is not checked in to multiple times in a row, you can configure Dash to remove the meeting
      room from all further meetings in the series.  Choose <i>Don't release</i> leave future meetings alone, or choose
      the number of consecutive missed check-ins after which the room will be removed from the series.
    </p>

    <h3>Allow the room to be booked</h3>
    <p>
      If <i>Yes</i>, then users will be allowed to book the room for impromptu (instant meetings).  Instant meetings
      start immediately, and run for the duration chosen by the user.
    </p>

    <h3>Allow the room to be booked in advance</h3>
    <p>
      Used in conjunction with the <i>Allow the room to be booked</i> option, if <i>Yes</i> is selected then users
      can book the room for times in the future using a calendar view.  Note that <i>Allow the room to be booked</i> must
      be enabled for this option to have any effect.
    </p>

    <h3>Booking passcode</h3>
    <p>
      A 4-digit number that users will need to enter in order to book meetings from the display.  This setting
      affects impromptu/instant and future bookings, as well as bookings made from the find a free room feature.
    </p>

    <h3>Require booking passcode when ending or extending meetings</h3>
    <p>
      If set to <i>Yes</i> then the booking passcode will also be required when making changes to meetings such as
      extending or ending them early.  This setting has no effect if a booking passcode is not set.
    </p>

    <h3>Suggested booking lengths</h3>
    <p>
      Control the suggested times that Dash displays for impromptu bookings. Choose <i>Friendly booking end times</i> to
      create bookings that finish at round times (such as on the hour). Choose <i>Friendly booking lengths</i> to create
      bookings that have round lengths (such as 30 minutes or one hour).
    </p>

    <h3>Include video conferencing</h3>
    <p>
      Controls whether Dash includes a video conferencing link in booking made from the display.  The videoconferencing
      provider used will be the default one you have set in Microsoft 365 or Google Workspace.  Including videoconferencing
      links is only supported when Dash is connected directly to Microsoft 365 or Google Workspace - it is not supported when connected to the device's calendar.
    </p>
    <ul>
      <li><b>Always</b> - Dash will always include a videoconferencing link in bookings made from the display</li>
      <li><b>Never</b> - Dash will never include a videoconferencing link in bookings made from the display</li>
      <li><b>Per booking</b> - Dash will ask each time a booking is made from the display</li>
    </ul>


    <h3>Users can add attendees when booking</h3>
    <p>
      If <i>Yes</i> then users can add attendees to meetings when booking from the display.  They can choose from the
      company directory, or invite external users by entering an email address.  Attendees will be given a Teams invite
      for video conferencing.
    </p>
    <p class="tip icon">Please note that adding attendees is only supported when using the <i>Microsoft 365 Enterprise
      Application</i> and <i>Google Service Account</i> connection methods, either configured in the Dashboard or locally on the device.</p>

    <h3>Maximum booking length</h3>
    <p>
      The maximum amount of time that impromptu/instant bookings can be.  The possible values are '15 minutes', '30 minutes',
      '1 hour', and '2 hours'.  Note that if you've set <i>Suggested booking lengths</i> to
      <i>Friendly booking end times</i> then Dash will round the booking lengths, which may result in a maximum length
      slightly longer than the value you've chosen here.
    </p>

    <h3>Users can set a message when booking instant meetings</h3>
    <p>
      If <i>Optional</i>, then users will be able to choose a subject and description when booking impromptu/instant meetings from the display.
      If <i>Required</i>, then users will be required to choose a meeting subject and may optionally enter a description.
    </p>

    <h3>Users can set a message when booking later meetings</h3>
    <p>
      If <i>Optional</i>, then users will be able to choose a subject and description when booking later meetings from the display.
      If <i>Required</i>, then users will be required to choose a meeting subject and may optionally enter a description.
    </p>

    <h3>Users can set the organizer when booking instant meetings</h3>
    <p>
      If <i>Optional</i> then users can set the meeting organizer when booking an impromptu/instant meeting from the display.
      If <i>Required</i> then users must choose an organizer when booking.  The organizer can be chosen from the
      company directory.  The booking will be created in their calendar, and the room will be invited to the meeting.
    </p>

    <h3>Users can set the organizer when booking later meetings</h3>
    <p>
      If <i>Yes</i> then users can set the meeting organizer when booking later meetings from the display.
      If <i>Required</i> then users must choose an organizer when booking.  The organizer can be chosen from the
      company directory.  The booking will be created in their calendar, and the room will be invited to the meeting.
    </p>
    <p class="tip icon">Please note that setting meeting organizers is only supported when using the <i>Microsoft 365 Enterprise
      Application</i> and <i>Google Service Account</i> connection methods, either configured in the Dashboard or locally on the device.</p>

    <h3>Allow ending meetings</h3>
    <p>
      If yes, then users can end the current meeting by tapping an <i>End now</i> button on the main display.
    </p>

    <h3>When ending</h3>
    <p>
      Used in conjunction with <i>Allow ending meetings</i>, this setting controls how Dash ends your meetings:
    </p>
    <ul>
      <li><b>Change the meeting finish time</b> - The finish time of the meeting is changed so that the meeting remains
        on the calendar, but finishes early</li>
      <li><b>Delete the meeting</b> - The meeting is removed entirely from the room's calendar when it is ended early</li>
    </ul>

    <h3>Allow extending meetings</h3>
    <p>
      If <i>Yes</i>, then users can extend the current meeting (if there is space available in the room's calendar) by
      pressing an <i>Extend</i> button on the main display.
    </p>

    <h3>Find free rooms</h3>
    <p>
      If <i>Yes</i> then users will be able to display a list of currently free/unoccupied meeting rooms.  Control the list
      of rooms that Dash checks using the <i>Rooms to check</i> option.
    </p>

    <h3>Rooms to check</h3>
    <p>
      A list of rooms that Dash will check when the <i>Find free rooms</i> feature is used.  Rooms in this list that are
      currently unoccupied will be shown to the user, and they will be able to book those rooms from the Dash display.
    </p>
    <p>
      Specify the list of room calendars to check by their IDs.  You can find the IDs for your room calendars by following
      the instructions in the <a routerLink="." fragment="calendars">Configuring calendars</a> section of this guide.
    </p>

    <h2 class="sectionHeader">Appearance and branding</h2>
    <h3>Background image</h3>
    <p>
      Automatically apply a background image to your Dash display devices by uploading an image or entering a URL.  Each Dash device will
      download and display the image, so if you enter a URL you will need to make sure that it is accessible to your Dash devices.
    </p>
    <p class="tip icon">To find out the ideal size of background image for your particular display device, open the
      <i>Appearance and branding</i> section of Dash's settings.  You should see the ideal image size displayed under the
      <i>Background image</i> option.
    </p>
    <h3>Logo image</h3>
    <p>
      Automatically apply a logo image to your Dash display devices by uploading an image or entering a URL.  The logo image will be displayed
      in the top left corner of your display device.
    </p>
    <h3>Logo size</h3>
    <p>
      Choose between three different sizes for your logo (small, medium, and large).
    </p>
    <p class="tip icon">
      If you would like to customize the size or position of your logo further, we recommend creating a background image with
      your logo in the size and position you would like, and using this instead of a separate logo image.
    </p>

    <h2>Branding colors</h2>
    <p>
      The following options control the color of Dash's screens and components.  You can create a cohesive branding look
      by changing these colors.  You can set a color by either entering the color's hex value (e.g. <span class="code">#FFFFFF</span>), or
      by clicking on the color preview on the right hand size of the color option and choosing a color in the picker.
    </p>
    <app-illustration alt="Selecting a custom color in Dash Enterprise" class="illustration colors" src="/assets/images/dash-enterprise-choose-color.png" webpSrc="/assets/images/dash-enterprise-choose-color.webp" width="713" height="389"></app-illustration>
    <h3>You can set the following colors in Dash</h3>
    <ul>
      <li><b>Background color</b> - The background color of the main display</li>
      <li><b>Text color</b> - The color of text displayed on the main display</li>
      <li><b>Button color</b> - The background color of buttons</li>
      <li><b>Button text color</b> - The text displayed inside buttons</li>
      <li><b>Button border color</b> - The border color of buttons (use transparent <span class="code">#00000000</span> to have no border around buttons)</li>
      <li><b>Dialog background color</b> - The background color of dialogs for room booking, find free rooms etc.</li>
      <li><b>Room free color</b> - The color displayed on the schedule view when the room is currently unoccupied</li>
      <li><b>Room almost booked color</b> - The color displayed on the schedule view when the room is currently unoccupied, but will be busy within the next 10 minutes</li>
      <li><b>Room booked color</b> - The color displayed on the schedule view when the room is currently busy</li>
      <li><b>Schedule background color</b> - When you have set the room occupancy to be shown on a border around the display or the background of the whole display, this setting controls the background color of the schedule view</li>
    </ul>

  </div>
</div>
